import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, useCallback } from 'react'
import * as c from 'common/c'
import colors from 'common/colors'
import { Alert, Box, Button } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import {
  getSubscriptionDetails,
  updateSubscriptionStatus,
} from 'api/subscriptions'
import { subscriptionPaymentFetched } from 'actions/subscriptions'
import { createCheckoutSession } from 'api/subscriptions'
import { unitConvert } from '@farmtrx/units'
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material'
import { FarmtrxIconCircleCheck } from '@farmtrx/farmtrx-icons'
import { subscriptionStatus } from '@farmtrx/options'
import { formatCurrency } from 'common/currencyUtils'

export default function SubscriptionComponent() {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const userPrefs = useSelector((state) => state.account.singleton.prefs)
  const subscriptions = useSelector((state) => state.account.subscriptions)
  const permissions = useSelector((state) => state.account.permissions)
  const customClaims = useSelector((state) => state.account.customClaims)

  const [subscriptionDetails, setSubscriptionDetails] = useState([])

  const isFarmTrxAdmin =
    permissions.includes('farmtrx:admin') || customClaims?.[c.FT_ADMIN_CLAIM]

  useEffect(() => {
    dispatch(getSubscriptionDetails())
  }, [])

  useEffect(() => {
    if (subscriptions.length) {
      setSubscriptionDetails(subscriptions[0]['subscriptionPeriods'])
    }
  }, [subscriptions])

  const setCurrentStatusMsg = useCallback(() => {
    let status = ''
    // Check the status based on the latest record out of all the subscription periods
    if (subscriptionDetails.length) {
      let maxDate = new Date(
        Math.max(...subscriptionDetails.map((e) => new Date(e.startDate))),
      )
        .toISOString()
        .split('T')[0]
      let sp = subscriptionDetails.find((s) => s.startDate === maxDate)

      if (!sp) {
        status = 'unknown'
      } else {
        if (sp.subscriptionStatus === subscriptionStatus.NotPaid) {
          status = 'inactive'
        } else if (
          (sp.subscriptionStatus === subscriptionStatus.PaymentDue ||
            sp.subscriptionStatus === subscriptionStatus.PaymentDueFinal) &&
          new Date(Date.now()).toISOString().split('T')[0] > sp.startDate
        ) {
          status = 'inactive'
        } else if (sp.subscriptionStatus === subscriptionStatus.New) {
          status = 'new'
        } else if (
          sp.subscriptionStatus === subscriptionStatus.PaymentDue ||
          sp.subscriptionStatus === subscriptionStatus.PaymentDueFinal
        ) {
          status = 'due'
        } else if (
          sp.subscriptionStatus === subscriptionStatus.Paid ||
          sp.subscriptionStatus === subscriptionStatus.FreeYear ||
          sp.subscriptionStatus === subscriptionStatus.Comped
        ) {
          status = 'paid'
        }
      }

      switch (status) {
        case 'new':
          return (
            <Alert severity="info">
              {t('new_subscription_status_message')}
            </Alert>
          )
        case 'due':
          return (
            <Alert severity="info">
              {t('payment_due_premium_status_message', {
                expiryDate: sp.startDate,
              })}
            </Alert>
          )
        case 'inactive':
          return (
            <Alert severity="warning">
              {t('inactive_premium_status_message')}
            </Alert>
          )
        case 'unknown':
          return <></>
        default:
          return (
            <Alert severity="success" icon={<FarmtrxIconCircleCheck />}>
              {t('active_premium_status_message')}
            </Alert>
          )
      }
    }
  })

  const columns = [
    {
      field: 'subscription_period',
      headerName: t('subscription_period'),
      minWidth: 200,
      flex: 1,
      renderCell: (params) => {
        return `${`${params.row.startDate} - ${params.row.endDate}`}`
      },
    },
    {
      field: 'status',
      headerName: t('status'),
      minWidth: 200,
      flex: 1,
      renderCell: (params) => {
        if (subscriptions[0]?.partnerTenantId) {
          let partnerStatus = params.row.subscriptionStatus
          if (
            partnerStatus === subscriptionStatus.PaymentDue ||
            partnerStatus === subscriptionStatus.PaymentDueFinal
          ) {
            // This should not happen since partner tenants do not get set to these subscription statuses,
            // but if updated manually or if a tenant is moved to a partner after a subcription period has been
            // set to one of these statuses, handle this as not paid.
            partnerStatus = subscriptionStatus.NotPaid
          }
          return t(partnerStatus)
        } else if (
          params.row.subscriptionStatus === subscriptionStatus.PaymentDue ||
          params.row.subscriptionStatus ===
            subscriptionStatus.PaymentDueFinal ||
          params.row.subscriptionStatus === subscriptionStatus.New
        ) {
          if (params.row.paymentInfo && params.row.paymentInfo.amountToPay) {
            return (
              <Button
                onClick={() => getCheckoutSession(params.row)}
                variant="contained"
                color="secondary"
                endIcon={<ArrowForwardIcon />}
                style={{
                  display: showSubscriptionActivationLink(params.row),
                }}
              >
                {`${t('pay_now')} (${formatCurrency(
                  params.row.paymentInfo.amountToPay,
                  params.row.paymentInfo.currency,
                )})`}
              </Button>
            )
          } else {
            return (
              <Button
                color="secondary"
                sx={{
                  padding: '0',
                  textTransform: 'initial !important',
                  textAlign: 'left',
                  display: 'block',
                  whiteSpace: 'break-spaces',
                }}
              >
                {t(params.row.paymentInfo?.message)}
              </Button>
            )
          }
        } else {
          return t(params.row.subscriptionStatus)
        }
      },
    },
    {
      field: 'area_under_management',
      headerName: t('area_under_management'),
      minWidth: 200,
      flex: 1,
      headerClassName: 'data-grid--header',
      renderCell: (params) => {
        return (
          params &&
          userPrefs &&
          `${unitConvert
            .convertAreaToPref(
              params.row.harvestedArea,
              userPrefs?.['units']['area_in'],
            )
            .toFixed(1)} ${userPrefs?.['units']['area_in']}`
        )
      },
    },
    ...(isFarmTrxAdmin
      ? [
          {
            field: '',
            headerName: 'Admin Actions',
            minWidth: 150,
            flex: 1,
            headerClassName: 'data-grid--header',
            renderCell: (params) => {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <Button
                    onClick={() =>
                      updateSubscriptionStatusHandler(
                        params.row,
                        subscriptionStatus.Comped,
                      )
                    }
                    variant="contained"
                    color="primary"
                    sx={{
                      display: 'inline-flex',
                    }}
                    disabled={
                      params.row.subscriptionStatus ===
                        subscriptionStatus.FreeYear ||
                      params.row.subscriptionStatus ===
                        subscriptionStatus.Paid ||
                      params.row.subscriptionStatus ===
                        subscriptionStatus.Comped
                    }
                  >
                    Set to Comped
                  </Button>
                  <Button
                    onClick={() =>
                      updateSubscriptionStatusHandler(
                        params.row,
                        subscriptionStatus.Paid,
                      )
                    }
                    variant="contained"
                    color="primary"
                    sx={{
                      display: 'inline-flex',
                    }}
                    disabled={
                      params.row.subscriptionStatus ===
                        subscriptionStatus.FreeYear ||
                      params.row.subscriptionStatus ===
                        subscriptionStatus.Paid ||
                      params.row.subscriptionStatus ===
                        subscriptionStatus.Comped
                    }
                  >
                    Set to Paid
                  </Button>
                </Box>
              )
            },
          },
        ]
      : []),
  ]

  const showSubscriptionActivationLink = useCallback((row) => {
    if (subscriptionDetails.length) {
      if (subscriptions[0]?.partnerTenantId) {
        return 'none'
      } else {
        if (
          row.subscriptionStatus === subscriptionStatus.FreeYear ||
          row.subscriptionStatus === subscriptionStatus.Paid ||
          row.subscriptionStatus === subscriptionStatus.Comped
        ) {
          return 'none'
        } else if (
          row.subscriptionStatus === subscriptionStatus.New ||
          row.subscriptionStatus === subscriptionStatus.PaymentDue ||
          row.subscriptionStatus === subscriptionStatus.PaymentDueFinal
        ) {
          return 'inline-flex'
        }
      }
    }
    return 'none'
  })

  const updateSubscriptionStatusHandler = useCallback(
    (row, newStatus) => {
      dispatch(updateSubscriptionStatus(row.id, newStatus)).catch((error) => {
        console.error('Failed to update subscription status:', error)
      })
    },
    [dispatch],
  )

  function CustomNoRowsOverlaySubscriptions() {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {t('no_subscriptions_found')}
      </div>
    )
  }

  function getCheckoutSession(row) {
    let finalObj = {}
    const subscriptionPeriodId = row.id
    finalObj.successUrl = window.location.origin + '/admin/subscription'
    finalObj.cancelUrl = window.location.origin + '/admin/subscription'

    dispatch(createCheckoutSession(subscriptionPeriodId, finalObj))
      .then((response) => {
        if (response) {
          dispatch(subscriptionPaymentFetched(response))
          setTimeout(() => {
            let url = response.url

            window.location.replace(url)
          }, 500)
        }
      })
      .catch((error) => {
        console.error('Failed to create checkout session:', error)
        alert(
          t('checkout_session_creation_failed_message') +
            ' ' +
            t('payment_contact_support'),
        )
      })
  }

  return (
    <Box
      sx={{
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        height: '100%',
      }}
    >
      <h1>{t('farmtrx_premium')}</h1>
      {subscriptionDetails?.length ? setCurrentStatusMsg() : null}
      <div
        style={{
          flexGrow: 1,
          height: '100%',
          width: '100%',
          maxHeight: '800px',
          marginTop: '2rem',
        }}
      >
        <DataGrid
          autoHeight
          slots={{
            noRowsOverlay: CustomNoRowsOverlaySubscriptions,
          }}
          rows={subscriptionDetails.map((i) => {
            return i
          })}
          columns={columns}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: colors.greyBackground,
              textTransform: 'uppercase',
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
        />
      </div>
    </Box>
  )
}
